import { useState } from 'react';
import { IconPlayerPlayFilled } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { youtubeVideoId } from '@/utils/youtube';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import Hint from './Hint';
import VideoModal from './VideoModal';

/**
 * A small round play button that opens a YouTube help video in a window — drop it next to whatever the video explains.
 *
 *   <VideoHelpButton url="https://youtu.be/UB1lSXEvySc" title="How to share" />
 *
 * `url` is any YouTube link (youtu.be/…, youtube.com/watch?v=…); nothing is drawn when it is missing or not a YouTube video.
 * `title` names the video: it heads the window and completes the button's label for screen readers.
 */
export default function VideoHelpButton({ url, title, className }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const id = youtubeVideoId(url);
    if (!id) return null;

    const label = t('common.watchVideo');
    return (
        <>
            <Hint label={label}>
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    aria-label={title ? `${label}: ${title}` : label}
                    className={cn(
                        'inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-primary/10 text-primary transition-colors hover:bg-primary/20 dark:bg-primary/20 dark:hover:bg-primary/30',
                        className,
                    )}
                >
                    <IconPlayerPlayFilled size={13} />
                </button>
            </Hint>
            <VideoModal open={open} onClose={() => setOpen(false)} videoId={id} title={title} />
        </>
    );
}
